/*
 * The fixed Cleo identity seat — TH1 (2026-08-30). One source for the
 * interface family, the mark, and the voice/machine typography. Every color
 * value derives from the contract's Cleo slot (contract.ts, the enacted
 * D-CI1 recommended two-constant shape); this module adds only the seat
 * vocabulary and the stylesheet projection. Adding or renaming a variable
 * here is a shared file-format change (Cleo Code + Cockpit), never app-local.
 */
import {
  CLEO_IDENTITY_SOURCE_HEX,
  CLEO_MARK_SOURCE_HEX,
  CLEO_CSS_VARIABLES,
  resolveCleoIdentity,
} from "./contract"

/*
 * Executable fork guard: the recorded pinned mark is #DD4300 (dated
 * 2026-09-02, the orange re-articulation; #5BAD97 before it)
 * (cleo-mark-one-instrument; the same hex is pinned in Cleo Code's
 * cleoMarkGeometry.ts). If the contract's mark ever drifts from this record,
 * the module refuses to load — the next fork arrives loudly, not silently.
 */
// DATED 2026-09-14: the recorded pinned mark is #DA7210 (GOALS/GOALS-DESIGN.html#hig5;
// #DD4300 from 2026-09-02 and #5BAD97 before it).
if (CLEO_MARK_SOURCE_HEX.mark.toUpperCase() !== "#DA7210") {
  throw new Error(
    `cleo-identity: contract mark ${CLEO_MARK_SOURCE_HEX.mark} drifted from the recorded pinned mark #DA7210 — re-articulate by dated ruling, never silently`,
  )
}

/** The fixed identity vocabulary: interface family, mark, and typography. */
export const CLEO_IDENTITY_CSS_VARIABLES = Object.freeze({
  identity: "--ncl-cleo-identity",
  identityDim: "--ncl-cleo-identity-dim",
  identityEdge: "--ncl-cleo-identity-edge",
  mark: "--ncl-cleo-mark",
  markField: "--ncl-cleo-mark-field",
  fontVoice: "--ncl-font-voice",
  fontMachine: "--ncl-font-machine",
} as const)

export type CleoIdentityCssVariable =
  (typeof CLEO_IDENTITY_CSS_VARIABLES)[keyof typeof CLEO_IDENTITY_CSS_VARIABLES]

/*
 * Typography contract: Cleo's voice reads in the serif face, machine text in
 * the mono face. The stacks name the contract faces first (self-hosting the
 * files is TH4's leg); the fallbacks keep every render legible before then.
 */
export const CLEO_FONT_VOICE_STACK =
  '"Source Serif 4", "Iowan Old Style", Georgia, serif'
export const CLEO_FONT_MACHINE_STACK =
  '"IBM Plex Mono", ui-monospace, "SF Mono", Menlo, monospace'

/**
 * Project the complete fixed seat: the identity family (the interface teal
 * ramp), the mark on its field, the typography contract, and the contract's
 * Cleo channel variables — one definition site for the whole Cleo slot.
 */
export function resolveCleoIdentitySeat(): Readonly<Record<string, string>> {
  return Object.freeze({
    [CLEO_IDENTITY_CSS_VARIABLES.identity]: CLEO_IDENTITY_SOURCE_HEX.light,
    [CLEO_IDENTITY_CSS_VARIABLES.identityDim]: CLEO_IDENTITY_SOURCE_HEX.mid,
    [CLEO_IDENTITY_CSS_VARIABLES.identityEdge]: CLEO_IDENTITY_SOURCE_HEX.deep,
    [CLEO_IDENTITY_CSS_VARIABLES.fontVoice]: CLEO_FONT_VOICE_STACK,
    [CLEO_IDENTITY_CSS_VARIABLES.fontMachine]: CLEO_FONT_MACHINE_STACK,
    // The mark and its field arrive with the contract's Cleo channel below
    // — one definition site; naming them twice was value-identical dead
    // weight that consumer builds refuse as TS2783.
    ...resolveCleoIdentity(),
  })
}

/**
 * The stylesheet seam, generated from the seat so the shipped
 * cleo-identity.css can never drift from the contract: the package test
 * compares the file bytes against exactly this text.
 */
export function cleoIdentityCss(): string {
  const lines = Object.entries(resolveCleoIdentitySeat())
    .map(([variable, value]) => `  ${variable}: ${value};`)
    .join("\n")
  return `/* GENERATED by cleo-identity.ts (cleoIdentityCss) — do not hand-edit.\n * The fixed Cleo identity seat: regenerate via the package test's recorded\n * expectation after a dated contract re-articulation. */\n:root {\n${lines}\n}\n`
}

/** Seat the fixed identity on an element (the DOM witness path). */
export function applyCleoIdentitySeat(target: HTMLElement): void {
  for (const [variable, value] of Object.entries(resolveCleoIdentitySeat())) {
    target.style.setProperty(variable, value)
  }
}

/** Clear exactly the seat's variables. */
export function clearCleoIdentitySeat(target: HTMLElement): void {
  for (const variable of Object.keys(resolveCleoIdentitySeat())) {
    target.style.removeProperty(variable)
  }
}
